import _ from 'lodash';

import { DURATION_SECONDS } from 'assets/js/time';

const ONE_YEAR = DURATION_SECONDS.Year * 1000;

export const DefaultReservationFilterState = () => Object({
  active: true,
  end: null,
  includeAutocancelled: false,
  isLoan: null,
  isReturned: null,
  isOverdue: null,
  barcode: null,
  category: null,
  checkedIn: true,
  notCheckedIn: true,
  includeExcludedReservables: false,
  includeExcludedUsers: false,
  includeFilteredUserIfGuest: true,
  paidStatus: null,
  project: null,
  reloadNonce: 0, // Increment this to trigger a reload
  reservable: null,
  reservablesResponse: null, // list of reservable IDs returned from API filter
  responsesPromise: null,
  searchFormResponses: '',
  searchReservation: '',
  showFilters: false,
  siteId: null,
  start: null,
  statuses: null,
  supervisor: null,
  tags: null,
  usersResponse: null,
  user: null,
  usergroup: null,
});

// Define the Acutal Usage filters that extend Reservation filters.
export const DefaultAdminActualUsageSpecificFilterState = () => Object({
  hasReservation: null,
  identifier: null,
  inUse: true,
  mergeThreshold: 0,
});
export const DefaultAdminActualUsageFilterState = () => _.merge({},
  DefaultAdminActualUsageSpecificFilterState(),
  DefaultReservationFilterState(),
);

// Define the Invoice filters that extend Reservation filters.
export const DefaultAdminInvoiceSpecificFilterState = () => Object({
  active: true,
  autosend: null,
  billToEmail: null,
  billToName: null,
  dateByPayment: false,
  doesRecur: null,
  invoiceNumber: null,
  invoiceType: null,
  invoiceItemName: null,
  invoiceItemSKU: null,
  isOverdue: null,
  status: null,
  orderId: null,
  paymentAmount: null,
  paymentSource: null,
  paymentSuccess: null,
  porticoTransactionId: null,
  uploadedToQuickBooks: null,
});
export const DefaultAdminInvoiceFilterState = () => _.merge({},
  DefaultAdminInvoiceSpecificFilterState(),
  DefaultReservationFilterState(),
);

export const DefaultAdminReservablesFilterState = () => Object({
  searchString: '',
  searchBarcode: '',
  searchKeyword: '',
  show: false,
  status: false,
  tags: [],
  usergroup: -1,
  className: null,
});

// Define the Subscription filters that extend Reservation filters.
export const DefaultAdminSubscriptionSpecificFilterState = () => Object({
});
export const DefaultAdminSubscriptionFilterState = () => _.merge({},
  DefaultAdminSubscriptionSpecificFilterState(),
  DefaultReservationFilterState(),
);

// DefaultProjectFilterState is never a made as default entry into the state
// because of the admin/user dynamic, so this is always declared externally
// using CreateNamespace.
export const DefaultProjectFilterState = () => _.merge({},
  {
    dateColumn: 'created',
    onEnd: false,
    onStart: false,
    searchString: '',
    searchStringLimited: '',
    status: null,
    projectTagIds: [],
  },
  DefaultReservationFilterState());

const defaults = {
  adminActualUsageFilterState: DefaultAdminActualUsageFilterState,
  adminInvoiceFilterState: DefaultAdminInvoiceFilterState,
  adminReservablesFilterState: DefaultAdminReservablesFilterState,
  adminSubscriptionFilterState: DefaultAdminSubscriptionFilterState,
  reservationFilterState: DefaultReservationFilterState,
};
const defaultKeys = _.keys(defaults);

const state = {
  adminActualUsageFilterState: DefaultAdminActualUsageFilterState(),
  adminInvoiceFilterState: DefaultAdminInvoiceFilterState(),
  adminReservablesFilterState: DefaultAdminReservablesFilterState(),
  adminSubscriptionFilterState: DefaultAdminSubscriptionFilterState(),
  reservationFilterState: DefaultReservationFilterState(),
};

const getters = {
  // FilterDateRangeTooGreat returns true if the date range is
  // greater than one year.
  FilterDateRangeTooGreat: (stateArg) => ((namespace) => {
    const start = stateArg[namespace].start;
    const end = stateArg[namespace].end;
    if (_.isNull(start) || _.isNull(end)) return false;
    if (!start.dateMoment || !end.dateMoment) return false;

    const result = (start.dateMoment - end.dateMoment) > ONE_YEAR;
    return result;
  }),

  FilterPrettyDateRange: (stateArg) => ((namespace) => {
    const start = stateArg[namespace].start;
    const end = stateArg[namespace].end;
    if (_.isNull(start) || _.isNull(end)) return '';
    if (!start.dateMoment || !end.dateMoment) return '';

    const startFmt = start.dateMoment.format('D MMMM YYYY');
    const endFmt = end.dateMoment.format('D MMMM YYYY');
    const result = `${startFmt} - ${endFmt}`;
    return result;
  }),

  ReservationFilterOtherFiltersPretty: (stateArg) => {
    const userStr = stateArg.reservationFilterState.user ?
      `User ${stateArg.reservationFilterState.user.display_name}` : '';
    const supervisorStr = stateArg.reservationFilterState.supervisor ?
      'All users under supervisor ' +
        `${stateArg.reservationFilterState.supervisor.display_name}` : '';
    const usergroupStr = stateArg.reservationFilterState.usergroup ?
      `All users in group ${stateArg.reservationFilterState.usergroup.name}` :
      '';
    let resourceStr = '';
    if (!_.isArray(stateArg.reservationFilterState.reservable)) {
      resourceStr = stateArg.reservationFilterState.reservable ?
        `Resource ${stateArg.reservationFilterState.reservable.name}` : '';
    } else {
      resourceStr =
        stateArg.reservationFilterState.reservable.length > 0 ?
          `Resources ${
            _.map(stateArg.reservationFilterState.reservable,
              (resv) => resv.name).join(', ')}` : '';
    }
    const projectStr = stateArg.reservationFilterState.project ?
      `Project ${stateArg.reservationFilterState.project.name}` : '';
    const categoryStr = stateArg.reservationFilterState.category ?
      'Any resources matching category ' +
        `${stateArg.reservationFilterState.category}` : '';
    const tagsStr = stateArg.reservationFilterState.tags ?
      'Any resources matching tag(s) ' +
        `${stateArg.reservationFilterState.tags.join(', ')}` : '';
    const statusesStr = stateArg.reservationFilterState.statuses ?
      'Any status(es) in ' +
        `${stateArg.reservationFilterState.statuses.join(', ')}` : '';
    const searchStr = stateArg.reservationFilterState.searchReservation ?
      'Any text matching ' +
        `${stateArg.reservationFilterState.searchReservation}` : '';
    const searchFRStr = stateArg.reservationFilterState.searchFormResponses ?
      'Any form response matching ' +
        `${stateArg.reservationFilterState.searchFormResponses}` : '';

    // checkedInStr is only of interest when we differ from the default of
    // including both checked in and not.
    let checkedInStr = '';
    if (stateArg.reservationFilterState.checkedIn &&
      !stateArg.reservationFilterState.notCheckedIn) {
      checkedInStr = 'Checked-in reservations only.';
    } else if (!stateArg.reservationFilterState.checkedIn &&
      stateArg.reservationFilterState.notCheckedIn) {
      checkedInStr = 'Not checked-in reservations only.';
    } else if (!stateArg.reservationFilterState.checkedIn &&
      !stateArg.reservationFilterState.notCheckedIn) {
      checkedInStr = 'Neither checked-in nor not checked-in (no results).';
    }

    let excludedStr = '';
    if (stateArg.reservationFilterState.includeExcludedReservables) {
      excludedStr += 'Include excluded resources.';
    }
    if (stateArg.reservationFilterState.includeExcludedUsers) {
      excludedStr += 'Include excluded users.';
    }

    return _.reduce(
      [userStr, usergroupStr, supervisorStr, resourceStr, projectStr,
        categoryStr, tagsStr, statusesStr, searchStr, searchFRStr,
        checkedInStr, excludedStr],
      (sum, val) => {
        if (val && sum === '') return `${val}`;
        if (val) return `${sum}; ${val}`;
        return sum;
      },
      '');
  },
};

const actions = {
  // CreateNamespace initializes a namespace to be that of the default function
  // passed.
  CreateNamespace({ commit }, { namespace, defaultFn }) {
    commit('INIT_FILTER_STATE', { namespace, defaultFn });
  },

  // FilterReset resets the given namespace's filter state.
  FilterReset({ commit }, { namespace }) {
    commit('RESET_FILTER_STATE', { namespace });
  },

  // FilterResetNonDefaultNamespaces resets all non-default namespaces.
  FilterResetNonDefaultNamespaces({ commit }) {
    commit('RESET_FILTER_STATE_NON_DEFAULT');
  },

  // MutateFilterValue lets a user mutate the the namespaced filter state
  // by passing a key and a value.
  MutateFilterValue({ commit }, { key, value, namespace }) {
    if (!state[namespace]) {
      console.warn(`Unknown namespace ${namespace}` +
        'provided to MutateFilterValue');
      return;
    }
    if (!_.includes(_.keys(state[namespace]), key)) {
      console.warn(`unknown key '${key}' provided to MutateFilterValue'`);
      return;
    }
    commit('MUTATE_FILTER_STATE', { key, value, namespace });
  },
};

const mutations = {
  INIT_FILTER_STATE(startArg, { namespace, defaultFn }) {
    const stateCopy = startArg;

    // stateCopy[namespace] = defaultFn();
    _.set(stateCopy, namespace, defaultFn());
    defaults[namespace] = defaultFn;
  },

  MUTATE_FILTER_STATE(stateArg, { key, value, namespace }) {
    if (!stateArg[namespace]) {
      console.warn(`Unknown namespace ${namespace}` +
        'provided to MUTATE_FILTER_STATE');
      return;
    }
    _.set(stateArg[namespace], key, _.cloneDeep(value));
  },

  // RESET_FILTER_STATE resets the editor state.
  RESET_FILTER_STATE(stateArg, { namespace }) {
    const stateCopy = stateArg;
    if (!stateCopy[namespace]) {
      console.warn(`Unknown namespace ${namespace}` +
        'provided to RESET_FILTER_STATE');
      return;
    }
    const reloadNonce = stateCopy[namespace].reloadNonce;
    stateCopy[namespace] = defaults[namespace]();
    stateCopy[namespace].reloadNonce = reloadNonce;
  },

  RESET_FILTER_STATE_NON_DEFAULT(stateArg) {
    const stateCopy = stateArg;
    const nonDefaultKeys = _.difference(_.keys(stateCopy), defaultKeys);
    _.forEach(nonDefaultKeys, (key) => delete stateCopy[key]);
  },
};

export default { actions, getters, mutations, state };
